import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Bell, X, Send } from 'lucide-react';
import { disputeService } from '../services/disputeService';
import { formatNumeroCompraComAno } from '../utils/formatters';
import { campo } from '../design';
import { useConfirm } from './shared/ConfirmDialog';
import { confirmacaoDoLanceFechado, fraseDoLanceFechadoQueFalhou } from '../utils/disputaEscrita';

interface Props {
  session: any;
  onClose: () => void;
  showToast?: (msg: string, type?: 'success' | 'error') => void;
  /** Callback após enviar — pai recarrega sessão pra refletir lastBid atualizado. */
  onSent?: () => void;
}

const IPCOM_CNPJ = '06163889000104';

const fmtBRL4 = (v: any) => {
  const n = typeof v === 'number' ? v : v ? parseFloat(String(v)) : NaN;
  if (!isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
};

const fmtBRL2 = (v: any) => {
  const n = typeof v === 'number' ? v : v ? parseFloat(String(v)) : NaN;
  if (!isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

/**
 * Painel de decisão do lance fechado.
 *
 * ⛔ NASCEU DE UMA PERDA (Pederneiras 15/2026, 14/09/2026). O operador tinha ~4
 * minutos para digitar um número IRREVERSÍVEL e a tela não oferecia âncora
 * nenhuma: nem o total (ele digita unitário mas negocia total), nem a distância
 * do estimado, nem o que aconteceu em AFs parecidas. Mandou R$ 4.200 e ficou em
 * 3º de 17 — por R$ 21 do segundo. O vencedor fechou em R$ 2.950, **62% abaixo
 * do próprio número público da etapa aberta**.
 *
 * Não dá para mostrar o preço dos concorrentes: o lance é SELADO por lei e não
 * existe fonte. O que dá é mostrar bem o que se sabe — e o histórico, que é a
 * única pista sobre o comportamento de quem está do outro lado.
 */
function PainelDecisao({
  lim,
  valorDigitado,
  historico,
}: {
  lim: any;
  valorDigitado: number | null;
  historico: { n: number; casos: any[] } | null;
}) {
  const qtd = Number(lim?.quantity) > 0 ? Number(lim.quantity) : 1;
  const estimado = Number(lim?.estimatedPrice) > 0 ? Number(lim.estimatedPrice) : null;
  // `melhorAberturaAF` é o snapshot congelado na convocação; `melhorItem` é o
  // campo vivo, que a coleta sobrescreve com o vencedor final. Preferir o
  // snapshot evita mostrar o resultado no lugar do ponto de partida.
  const melhorAberto = Number(lim?.melhorAberturaAF ?? lim?.melhorItem) > 0
    ? Number(lim.melhorAberturaAF ?? lim.melhorItem)
    : null;
  const ultimo = Number(lim?.lastBid) > 0 ? Number(lim.lastBid) : null;
  const piso = Number(lim?.limitValue) > 0 ? Number(lim.limitValue) : null;

  const linhas = [
    { rot: 'Estimado do órgão', v: estimado },
    { rot: 'Melhor da etapa aberta', v: melhorAberto },
    { rot: 'Nosso último (aberta)', v: ultimo },
    { rot: 'Piso configurado', v: piso },
  ].filter((l) => l.v != null);

  const pctDoEstimado = valorDigitado != null && estimado ? (valorDigitado / estimado) * 100 : null;
  const pctDoMelhor = valorDigitado != null && melhorAberto ? (valorDigitado / melhorAberto) * 100 : null;

  return (
    <div className="mt-3 space-y-2 text-[11px]">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {linhas.map((l) => (
          <div key={l.rot} className="flex justify-between gap-2 min-w-0">
            <span className="text-slate-500 truncate">{l.rot}</span>
            <span className="font-mono text-slate-300 shrink-0">
              {fmtBRL4(l.v)}
              {qtd > 1 && <span className="text-slate-600"> · {fmtBRL2((l.v as number) * qtd)}</span>}
            </span>
          </div>
        ))}
      </div>

      {/* Simulação do que está digitado. O operador raciocina em TOTAL — cada
          real no unitário vale `qtd` reais no contrato — e digitava no escuro. */}
      {valorDigitado != null && valorDigitado > 0 && (
        <div className="bg-central-elevado/70 border border-central-borda rounded px-2 py-1.5 space-y-0.5">
          <div className="flex justify-between">
            <span className="text-slate-400">Total ({qtd}×)</span>
            <span className="font-mono text-purple-300 font-semibold">{fmtBRL2(valorDigitado * qtd)}</span>
          </div>
          {pctDoMelhor != null && (
            <div className="flex justify-between">
              <span className="text-slate-500">do melhor da aberta</span>
              <span className="font-mono text-slate-300">{pctDoMelhor.toFixed(1)}%</span>
            </div>
          )}
          {pctDoEstimado != null && (
            <div className="flex justify-between">
              <span className="text-slate-500">do estimado</span>
              <span className={`font-mono ${pctDoEstimado < 50 ? 'text-amber-300' : 'text-slate-300'}`}>
                {pctDoEstimado.toFixed(1)}%
              </span>
            </div>
          )}
          {/* Abaixo de 50% do estimado o pregoeiro costuma pedir demonstração de
              exequibilidade, com prazo curto. Não é impedimento — é preparo. Em
              14/09 a IPCOM mandou 43% do estimado sem nenhum aviso na tela. */}
          {pctDoEstimado != null && pctDoEstimado < 50 && (
            <div className="text-amber-300/90 leading-snug pt-1 border-t border-central-borda">
              ⚠ {pctDoEstimado.toFixed(0)}% do estimado — prepare a planilha de composição:
              nessa faixa o pregoeiro costuma exigir demonstração de exequibilidade.
            </div>
          )}
        </div>
      )}

      {/* O histórico. Mostra os CASOS e o n — nunca uma "mediana" sobre amostra
          pequena, que teria cara de medição sem ser uma. */}
      {historico && historico.n > 0 && (
        <div className="border-t border-central-borda pt-1.5">
          <div className="text-slate-500 mb-1">
            Nas AFs anteriores, o vencedor fechou abaixo do melhor da etapa aberta
            {historico.n < 5 && <span className="text-slate-600"> (só {historico.n} caso{historico.n > 1 ? 's' : ''} registrado{historico.n > 1 ? 's' : ''} — pouco para tirar média)</span>}:
          </div>
          <div className="space-y-0.5">
            {historico.casos.slice(0, 5).map((c: any) => (
              <div key={`${c.sessionId}-${c.melhorAbertura}`} className="flex justify-between gap-2">
                <span className="text-slate-500 truncate min-w-0">
                  {String(c.orgao).slice(0, 26)} {c.numeroCompra}
                </span>
                <span className="font-mono text-slate-300 shrink-0">
                  −{c.cortePct}% <span className="text-slate-600">({fmtBRL2(c.melhorAbertura)}→{fmtBRL2(c.vencedorFechado)})</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const parseValueBR = (s: string): number | null => {
  const cleaned = s.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
  const n = parseFloat(cleaned);
  return isFinite(n) ? n : null;
};

/**
 * Modal de Lance Final Fechado (Pregão Aberto/Fechado, item 5.11 do edital).
 *
 * Comportamento:
 *   - Inputs por item (ou 1 input agregado pro grupo) pra digitar o lance final.
 *   - Travas client-side: valor > 0, ≥ piso (limitValue), ≤ último lance.
 *   - "Manter último lance" / fechar sem submit = não envia (mantém último, 5.11.3).
 *   - Confirmação dispara POST /manual-bid no backend.
 *   - Som + Browser Notification quando abre.
 *
 * Aciona quando rankingData.itensGrupo['-1'][0].fase === 'LF' E
 * algum item tem convocado=true. Detecção feita pelo componente pai
 * (DisputeView) com state local pra não reabrir após dismiss.
 */
export function LanceFechadoModal({ session, onClose, showToast, onSent }: Props) {
  const confirmar = useConfirm();
  // Detecta itens convocados pra digitar valores (1 input por item; grupo
  // agrega num único).
  const items = useMemo(() => {
    const r = session?.rankingData;
    const igRaw = r?.itensGrupo?.['-1'] || r?.emDisputa;
    const rows: any[] = Array.isArray(igRaw)
      ? igRaw
      : igRaw?.registros || igRaw?.itens || igRaw?.items || [];
    return rows;
  }, [session?.rankingData]);

  const isGroup = items.length === 1 && items[0]?.tipo === 'G';

  // Inputs locais por itemNumber (em grupo, todos os limits compartilham
  // o lance — pega o primeiro do grupo só pra exibir descrição).
  const limits: any[] = useMemo(
    () => (session?.itemLimits || []) as any[],
    [session?.itemLimits],
  );
  const itemRowsForInput = useMemo(() => {
    if (isGroup) {
      // 1 input só pro grupo inteiro
      const first = limits[0];
      return first ? [first] : [];
    }
    return items.map((row: any) => {
      const lim = limits.find((l: any) => l.itemNumber === row.numero);
      return lim || { itemNumber: row.numero, description: row.descricao, quantity: 1 };
    });
  }, [items, isGroup, limits]);

  const [inputs, setInputs] = useState<Record<number, string>>(() => {
    const init: Record<number, string> = {};
    itemRowsForInput.forEach((il: any) => {
      init[il.itemNumber] = '';
    });
    return init;
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Histórico de corte do vencedor nas AFs anteriores. Carrega em paralelo e
  // FALHA EM SILÊNCIO de propósito: é apoio à decisão, e um erro de rede não
  // pode atrapalhar quem tem 4 minutos pra mandar um lance irreversível.
  const [historico, setHistorico] = useState<{ n: number; casos: any[] } | null>(null);
  useEffect(() => {
    let vivo = true;
    disputeService
      .afHistorico()
      .then((h) => { if (vivo) setHistorico(h); })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  // Countdown 5min ou até dataHoraFimContagem
  const fimContagem = useMemo(() => {
    const ts = items[0]?.dataHoraFimContagem;
    return ts ? new Date(ts).getTime() : null;
  }, [items]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const tempoRestanteMs = fimContagem ? Math.max(0, fimContagem - now) : null;
  const expirou = tempoRestanteMs !== null && tempoRestanteMs === 0;

  // Som + notificação quando abre
  useEffect(() => {
    try {
      const audio = new Audio(
        'data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=',
      );
      audio.volume = 0.7;
      audio.play().catch(() => {});
    } catch {}
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('IPCOM Hub — Convocado pra Fase Fechada', {
        body: `Pregão ${session?.item?.numeroCompra || ''} — você tem ${
          tempoRestanteMs ? Math.round(tempoRestanteMs / 60000) : 5
        }min pra dar o lance final`,
        tag: `lf-${session?.id}`,
        requireInteraction: true,
      });
    } else if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    const origTitle = document.title;
    let blink = false;
    const titleInterval = setInterval(() => {
      blink = !blink;
      document.title = blink ? '🔔 CONVOCADO — Fase Fechada' : origTitle;
    }, 1000);
    return () => {
      clearInterval(titleInterval);
      document.title = origTitle;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmtCountdown = (ms: number) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return `${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  };

  // Validação de cada input
  const validateValue = (val: string, lim: any): string | null => {
    if (!val.trim()) return null; // empty = "manter último", sem erro
    const n = parseValueBR(val);
    if (n == null || n <= 0) return 'Valor inválido';
    if (lim?.limitValue && Number(lim.limitValue) > 0 && n < Number(lim.limitValue)) {
      return `Abaixo do piso (R$ ${Number(lim.limitValue).toLocaleString('pt-BR')})`;
    }
    if (lim?.lastBid && Number(lim.lastBid) > 0 && n > Number(lim.lastBid)) {
      return `Maior que último lance (R$ ${Number(lim.lastBid).toLocaleString('pt-BR')}) — só desce`;
    }
    return null;
  };

  // Header do grupo: melhor / meu / convocados
  const groupHeader = items[0];
  const melhorGeral =
    groupHeader?.melhorValorGeral?.valorCalculado ??
    groupHeader?.melhorValorGeral?.valorInformado;
  const meuValor =
    groupHeader?.melhorValorFornecedor?.valorCalculado ??
    groupHeader?.melhorValorFornecedor?.valorInformado;

  const handleSubmit = async () => {
    setError(null);
    const toSend: Array<{ itemNumber: number; valor: number }> = [];
    for (const il of itemRowsForInput) {
      const v = inputs[il.itemNumber] || '';
      if (!v.trim()) continue; // pula vazio = manter último
      const err = validateValue(v, il);
      if (err) {
        setError(`Item #${il.itemNumber}: ${err}`);
        return;
      }
      const n = parseValueBR(v);
      if (n != null) toSend.push({ itemNumber: il.itemNumber, valor: n });
    }
    if (toSend.length === 0) {
      // Sem nenhum lance digitado = manter último (5.11.3). Fecha modal.
      onClose();
      return;
    }
    // D6 (Heitor, 12/10/2026): o envio pergunta, com item → valor; o botão era o próprio envio.
    if (!(await confirmar(confirmacaoDoLanceFechado(toSend)))) return;
    setSending(true);
    const enviados: number[] = [];
    try {
      for (const b of toSend) {
        await disputeService.manualBid(session.id, {
          itemNumber: b.itemNumber,
          valorInformado: b.valor,
          faseItem: 'LF',
        });
        enviados.push(b.itemNumber);
      }
      showToast?.(`${toSend.length} lance(s) fechado(s) enviado(s)`, 'success');
      onSent?.();
      onClose();
    } catch (err: any) {
      // D6: na falha no meio do laço, diz o que já saiu e o que não saiu (antes: só a mensagem do erro).
      const falhou = toSend[enviados.length]?.itemNumber;
      const naoEnviados = toSend.slice(enviados.length + 1).map((b) => b.itemNumber);
      setError(fraseDoLanceFechadoQueFalhou(enviados, falhou, err?.response?.data?.message || err?.message || 'erro desconhecido', naoEnviados));
      if (enviados.length) onSent?.();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-central-elevado border-2 border-purple-600 rounded-central shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600/30 to-fuchsia-600/30 border-b border-purple-700 px-6 py-4 flex items-center gap-3">
          <Bell className="text-purple-300 animate-pulse" size={24} />
          <div className="flex-1">
            <h2 className="text-xl font-bold text-purple-100">Fase Fechada — Convocado</h2>
            <p className="text-sm text-purple-300/80">
              Pregão Aberto/Fechado · {session?.item?.orgaoNome || '—'} · Nº{' '}
              {formatNumeroCompraComAno(session?.item?.numeroCompra, session?.item?.anoCompra)}
            </p>
          </div>
          {tempoRestanteMs !== null && (
            <div className={`text-right ${expirou ? 'text-red-400' : 'text-amber-300'}`}>
              <div className="text-3xl font-mono font-bold">
                {expirou ? 'EXPIRADO' : fmtCountdown(tempoRestanteMs)}
              </div>
              <div className="text-xs uppercase">restante</div>
            </div>
          )}
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200 ml-2"
            disabled={sending}
            title="Cancelar (mantém último lance)"
          >
            <X size={20} />
          </button>
        </div>

        {/* Resumo */}
        <div className="px-6 py-4 grid grid-cols-3 gap-4 border-b border-central-borda bg-central-cartao">
          <div>
            <div className="text-xs uppercase text-slate-500">Melhor (fase aberta)</div>
            <div className="text-lg font-mono font-bold text-slate-200">{fmtBRL4(melhorGeral)}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500">Meu último lance</div>
            <div className="text-lg font-mono font-bold text-emerald-300">{fmtBRL4(meuValor)}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500">Fase</div>
            <div className="text-lg font-bold text-purple-300">FECHADA (LF)</div>
            <div className="text-[10px] text-slate-500">envelope sigiloso · 1 lance</div>
          </div>
        </div>

        {/* Inputs por item */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="text-sm text-slate-400 mb-3">
            Digite o novo valor por {isGroup ? 'GRUPO' : 'item'}. Deixe em branco pra{' '}
            <strong className="text-slate-200">manter o último lance</strong> (5.11.3 do edital).
            Regras: valor só desce do último lance e nunca abaixo do piso configurado.
          </div>
          <div className="space-y-3">
            {itemRowsForInput.map((il: any) => {
              const val = inputs[il.itemNumber] || '';
              const errMsg = validateValue(val, il);
              return (
                <div
                  key={il.itemNumber}
                  className="bg-central-cartao border border-central-borda rounded-lg p-4"
                >
                  <div className="flex items-baseline justify-between gap-2 mb-2">
                    <div className="text-sm font-medium text-slate-200">
                      {isGroup ? 'Grupo (lance único)' : `Item #${il.itemNumber}`} —{' '}
                      <span className="text-slate-400 font-normal">{il.description}</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      Piso: <span className="font-mono text-slate-300">{fmtBRL4(il.limitValue)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={val}
                        onChange={(e) =>
                          setInputs({ ...inputs, [il.itemNumber]: e.target.value })
                        }
                        placeholder={`R$ ${il.lastBid ?? '—'} (manter)`}
                        className={`w-full focus:border-purple-500 rounded px-3 py-2 text-sm text-slate-100 font-mono ${campo}`}
                        disabled={sending || expirou}
                        autoFocus
                      />
                    </div>
                    {il.lastBid != null && Number(il.lastBid) > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          setInputs({
                            ...inputs,
                            [il.itemNumber]: String(il.lastBid).replace('.', ','),
                          })
                        }
                        className="text-xs text-slate-400 hover:text-slate-200 underline"
                        disabled={sending}
                        title="Preencher com o último lance da fase aberta"
                      >
                        usar último
                      </button>
                    )}
                  </div>
                  <PainelDecisao lim={il} valorDigitado={parseValueBR(val)} historico={historico} />
                  {errMsg && (
                    <div className="text-xs text-red-400 mt-1 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {errMsg}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-central-borda bg-central-cartao flex items-center justify-between gap-3">
          {error && (
            <div className="flex-1 text-sm text-red-400 flex items-center gap-1">
              <AlertCircle size={14} />
              {error}
            </div>
          )}
          <div className="flex-1" />
          <button
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 text-sm text-slate-300 hover:text-slate-100 rounded border border-central-borda hover:bg-central-elevado"
          >
            Manter último (não enviar)
          </button>
          <button
            onClick={handleSubmit}
            disabled={sending || expirou}
            className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-500 text-white rounded font-medium flex items-center gap-2 disabled:opacity-50"
          >
            <Send size={14} />
            {sending ? 'Enviando…' : 'Enviar lance(s)'}
          </button>
        </div>
      </div>
    </div>
  );
}
